import React, { useState, useEffect } from 'react';
import { Center, Spinner } from '@chakra-ui/react';
import {
  MMEmbeddableZone,
  useEmbeddedEffect,
} from '@machinemetrics/mm-react-embeddable';
import { palette } from '@m12s/component-library';

const HOLD_DELAY_MS = 500;
const FADE_DURATION_MS = 300;

/**
 * A wrapper around MMEmbeddableZone that shows a loading spinner
 * until the embedded app signals it's ready.
 *
 * After ready, holds the spinner for 500ms then fades it out.
 *
 * Accepts all props that MMEmbeddableZone accepts.
 */
const MMEmbeddableZoneWithLoader = ({ id, ...props }) => {
  const [isReady, setIsReady] = useState(false);
  const [isFading, setIsFading] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  useEmbeddedEffect(id, 'ready', () => {
    setIsReady(true);
  });

  useEffect(() => {
    if (!isReady) {
      return undefined;
    }

    // Hold for 500ms before starting fade
    const holdTimer = setTimeout(() => {
      setIsFading(true);
    }, HOLD_DELAY_MS);

    return () => {
      clearTimeout(holdTimer);
    };
  }, [isReady]);

  useEffect(() => {
    if (!isFading) {
      return undefined;
    }

    // After fade completes, fully hide the loader
    const fadeTimer = setTimeout(() => {
      setIsHidden(true);
    }, FADE_DURATION_MS);

    return () => {
      clearTimeout(fadeTimer);
    };
  }, [isFading]);

  return (
    <>
      {!isHidden && (
        <Center
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="white"
          zIndex={1}
          opacity={isFading ? 0 : 1}
          transition={`opacity ${FADE_DURATION_MS}ms ease-out`}
        >
          <Spinner
            w="48px"
            h="48px"
            color={palette.Indigo600}
            thickness="4px"
          />
        </Center>
      )}
      <MMEmbeddableZone id={id} {...props} />
    </>
  );
};

export default MMEmbeddableZoneWithLoader;
